import type { LocalDate } from '../time';
import {
  DAILY_CAP_PER_DIMENSION,
  DAILY_CAP_TOTAL,
  HORIZON_FROM,
  levelFromPoints,
  ligaForPoints,
  MILESTONES,
  nextLiga,
  RULESET_VERSION,
  seasonKeyFor,
  seasonRange,
  tramoForPoints,
  type LevelState,
  type Liga,
  type Milestone,
  type Tramo,
} from './rules';

/**
 * EL MOTOR DE PROGRESIÓN — una función pura (PRD 8.2).
 *
 * Pura de verdad: mismos eventos, mismo resultado, en el navegador y en el
 * servidor. Esa es la propiedad que permite que el cliente pinte el nivel al
 * instante y que el servidor lo selle después **sin que discrepen nunca**. Si
 * esto tuviera un `Date.now()` dentro, o dependiera del orden de llegada, el
 * usuario vería el número parpadear al sincronizar.
 *
 * Las ocho propiedades que los tests verifican para CUALQUIER historial (PRD 17.3):
 *
 *   P1 Monotonía      añadir un evento nunca reduce el total
 *   P2 Conmutatividad el orden de aplicación no altera el resultado
 *   P3 Idempotencia   aplicar el mismo evento dos veces == una vez
 *   P4 Solo crece     los desbloqueos de E ∪ {e} contienen los de E
 *   P5 Determinismo   dos ejecuciones con la misma entrada → salidas idénticas
 *   P6 Suelo          el nivel mostrado nunca baja del máximo histórico
 *   P7 Topes          ningún día aporta más que el tope diario
 *   P8 Cross-runtime  el resultado en Node == el resultado en el navegador
 */

/** La dimensión que recoge lo que no pertenece a un área: diario, ánimo, revisiones. */
export const GENERAL_DIMENSION = 'general';

export type ProgressionEvent = {
  /** Clave de idempotencia. Dos eventos con el mismo id son el mismo evento. */
  readonly id: string;
  readonly localDate: LocalDate;
  /** Clave del área de vida, o `general`. */
  readonly dimension: string;
  readonly points: number;
  /**
   * Si el reloj del dispositivo era plausible al escribir (PRD 8.5).
   * **Lo no atestado no da puntos.** No se borra ni se esconde: se enseña como
   * dato y no cuenta para la progresión. Es acotar el fraude, no impedirlo.
   */
  readonly attested: boolean;
};

export type DimensionState = {
  readonly key: string;
  readonly points: number;
  readonly level: LevelState;
};

export type SeasonState = {
  readonly key: string;
  readonly label: string;
  readonly from: LocalDate;
  readonly to: LocalDate;
  readonly points: number;
  readonly liga: Liga;
  readonly next: Liga | null;
  readonly toNext: number;
};

export type ProgressionState = {
  readonly rulesetVersion: number;
  readonly totalPoints: number;
  /** Áreas de vida, ordenadas por clave. `general` va aparte. */
  readonly dimensions: readonly DimensionState[];
  readonly general: DimensionState;
  /** ⌊ media de los niveles de área ⌋. `general` NO entra en la media. */
  readonly generalLevel: number;
  /** La media con decimal: es el progreso hacia el siguiente nivel general. */
  readonly generalMean: number;
  readonly tramo: Tramo;
  readonly nextTramo: Tramo | null;
  readonly toNextTramo: number;
  readonly unlocked: readonly Milestone[];
  readonly nextMilestone: Milestone | null;
  readonly toNextMilestone: number;
  /** `true` cuando el catálogo se acabó y toca la pieza de horizonte. */
  readonly beyondCatalogue: boolean;
  readonly season: SeasonState;
  /** Puntos por día ya con los topes aplicados. Alimenta el mapa de calor. */
  readonly byDay: ReadonlyMap<LocalDate, number>;
};

export type ComputeOptions = {
  /** El día lógico de hoy. Decide qué temporada está en curso. */
  readonly today: LocalDate;
  /**
   * Suelo sellado: el nivel general más alto que el usuario ha visto nunca.
   * Un trofeo que desaparece destruye la confianza en la app entera (PRD 8.4),
   * así que el nivel mostrado nunca baja de aquí aunque cambien las reglas.
   */
  readonly levelFloor?: number;
  /**
   * Hitos ya sellados. Se unen a los calculados y **nunca se quitan**: el
   * conjunto de desbloqueos solo crece.
   */
  readonly sealedMilestones?: readonly string[];
};

type DayBucket = Map<string, number>;

export function computeProgression(
  events: readonly ProgressionEvent[],
  options: ComputeOptions,
): ProgressionState {
  // --- P3 · idempotencia: el mismo id es el mismo evento ---------------------
  const unique = new Map<string, ProgressionEvent>();
  for (const event of events) {
    if (!event.attested) continue;
    if (event.points <= 0) continue;
    unique.set(event.id, event);
  }

  // --- agrupar por día; dentro del día, por dimensión ------------------------
  const days = new Map<LocalDate, DayBucket>();
  for (const event of unique.values()) {
    let bucket = days.get(event.localDate);
    if (!bucket) {
      bucket = new Map();
      days.set(event.localDate, bucket);
    }
    bucket.set(event.dimension, (bucket.get(event.dimension) ?? 0) + event.points);
  }

  // --- P7 · aplicar los topes -----------------------------------------------
  const lifetime = new Map<string, number>();
  const byDay = new Map<LocalDate, number>();
  const seasonPoints = new Map<string, number>();

  for (const [localDate, bucket] of days) {
    /**
     * El tope por dimensión primero, el total después. Y el total se reparte
     * **recorriendo las dimensiones por orden alfabético de clave**, no por
     * orden de llegada: si el tope de 150 se alcanza a mitad, tiene que
     * truncarse siempre la misma, o el resultado dependería de en qué orden
     * sincronizó el móvil. Eso rompería P2 y P5 a la vez.
     */
    const capped = [...bucket.entries()]
      .map(([dimension, raw]) => [dimension, Math.min(raw, DAILY_CAP_PER_DIMENSION)] as const)
      .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));

    let dayTotal = 0;
    for (const [dimension, points] of capped) {
      const room = DAILY_CAP_TOTAL - dayTotal;
      if (room <= 0) break;
      const granted = Math.min(points, room);
      dayTotal += granted;
      lifetime.set(dimension, (lifetime.get(dimension) ?? 0) + granted);
    }

    byDay.set(localDate, dayTotal);
    const season = seasonKeyFor(localDate);
    seasonPoints.set(season, (seasonPoints.get(season) ?? 0) + dayTotal);
  }

  // --- totales y niveles -----------------------------------------------------
  let totalPoints = 0;
  for (const points of lifetime.values()) totalPoints += points;

  const dimensions: DimensionState[] = [...lifetime.entries()]
    .filter(([key]) => key !== GENERAL_DIMENSION)
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .map(([key, points]) => ({ key, points, level: levelFromPoints(points) }));

  const generalPoints = lifetime.get(GENERAL_DIMENSION) ?? 0;
  const general: DimensionState = {
    key: GENERAL_DIMENSION,
    points: generalPoints,
    level: levelFromPoints(generalPoints),
  };

  /**
   * El nivel general es la media de los niveles de ÁREA, redondeada hacia abajo.
   * `general` queda fuera a propósito: si contara, escribir el diario todos los
   * días subiría el nivel «general» del usuario sin que ningún área suya
   * hubiera cambiado, y el número dejaría de significar lo que dice.
   */
  /**
   * La media es de los niveles ENTEROS, no del progreso dentro de cada uno.
   * El decimal que se enseña («6 · media 6,0») mide lo repartido que está el
   * usuario entre sus áreas, no lo cerca que está del siguiente nivel: con
   * niveles 7 · 6 · 5 la media es 6,0 y con 7 · 7 · 5 es 6,3. Eso es lo que el
   * paso 7 aprobó en pantalla.
   */
  const mean =
    dimensions.length === 0
      ? // Sin ningún área todavía. Los niveles empiezan en 1, no en 0: el día uno
        // el usuario ya está en el camino, no fuera de él.
        1
      : dimensions.reduce((sum, d) => sum + d.level.level, 0) / dimensions.length;

  const generalLevel = Math.max(options.levelFloor ?? 0, Math.floor(mean));

  // --- P4 · los desbloqueos solo crecen --------------------------------------
  const sealed = new Set(options.sealedMilestones ?? []);
  const unlocked = MILESTONES.filter((m) => totalPoints >= m.points || sealed.has(m.key));
  const unlockedKeys = new Set(unlocked.map((m) => m.key));
  const nextMilestone = MILESTONES.find((m) => !unlockedKeys.has(m.key)) ?? null;

  // --- tramo ------------------------------------------------------------------
  const tramo = tramoForPoints(totalPoints);
  const tramoIndex = tramo.key;
  const nextTramo = tramo.to === null ? null : (tramoForPoints(tramo.to) ?? null);

  // --- temporada en curso -----------------------------------------------------
  const seasonKey = seasonKeyFor(options.today);
  const range = seasonRange(seasonKey);
  const points = seasonPoints.get(seasonKey) ?? 0;
  const liga = ligaForPoints(points);
  const next = nextLiga(points);

  return {
    rulesetVersion: RULESET_VERSION,
    totalPoints,
    dimensions,
    general,
    generalLevel,
    generalMean: Math.round(mean * 10) / 10,
    tramo,
    nextTramo: nextTramo && nextTramo.key !== tramoIndex ? nextTramo : null,
    toNextTramo: tramo.to === null ? 0 : Math.max(0, tramo.to - totalPoints),
    unlocked,
    nextMilestone,
    toNextMilestone: nextMilestone ? Math.max(0, nextMilestone.points - totalPoints) : 0,
    beyondCatalogue: totalPoints >= HORIZON_FROM,
    season: {
      key: seasonKey,
      label: range.label,
      from: range.from,
      to: range.to,
      points,
      liga,
      next,
      toNext: next ? Math.max(0, next.from - points) : 0,
    },
    byDay,
  };
}

export type SealedSeason = {
  readonly key: string;
  readonly label: string;
  readonly from: LocalDate;
  readonly to: LocalDate;
  readonly points: number;
  readonly liga: Liga;
  /** Hitos alcanzados DENTRO de la temporada. */
  readonly milestones: number;
};

/**
 * Sella una temporada ya cerrada para la sala de trofeos. Usa la misma función
 * que el resto: la sala no tiene aritmética propia.
 *
 * Los hitos son los que se cruzaron **dentro** de la temporada, y por eso hace
 * falta todo el historial y no solo el trozo: un hito depende del acumulado de
 * por vida, así que se mira cuántos había antes de que empezara y cuántos al
 * acabar. Contar solo con los puntos del trimestre daría siempre los primeros
 * del catálogo, que es justo lo que el usuario ya había pasado hace un año.
 */
export function sealSeason(
  allEvents: readonly ProgressionEvent[],
  seasonKey: string,
): SealedSeason {
  const range = seasonRange(seasonKey);
  const attested = allEvents.filter((e) => e.attested);

  const before = computeProgression(
    attested.filter((e) => e.localDate < range.from),
    { today: range.from },
  );
  const through = computeProgression(
    attested.filter((e) => e.localDate <= range.to),
    { today: range.to },
  );
  const inSeason = computeProgression(
    attested.filter((e) => e.localDate >= range.from && e.localDate <= range.to),
    { today: range.to },
  );

  return {
    key: seasonKey,
    label: range.label,
    from: range.from,
    to: range.to,
    points: inSeason.totalPoints,
    liga: ligaForPoints(inSeason.totalPoints),
    milestones: through.unlocked.length - before.unlocked.length,
  };
}
